import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert } from 'antd';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import SelectionBar from '@components/Gui/SelectionBar';

import EventBlock from './EventBlock';
import ScopeFilters from './ScopeFilters';
import { useSendProjectDrawer } from './SendProjectDrawerContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * EventsSelectionWrapper is the first step of the drawer: the filters rail on the left,
 * the models to include on the right, the send button in the footer.
 */
const EventsSelectionWrapper = ({ onClose, onGenerateDocVersions }) => {
    const { state, actions, visibleEventsList, selectionSummary } = useSendProjectDrawer();
    const { errorSending, isSending } = state;

    const listRef = useRef(null);
    const [hasMoreBelow, setHasMoreBelow] = useState(false);

    const hasSelection = selectionSummary.models > 0;

    /**
     * The list scrolls under the footer: tell the user when something is left below
     */
    const updateScrollHint = useCallback(() => {
        const list = listRef.current;
        if (list) {
            setHasMoreBelow(list.scrollHeight - list.scrollTop - list.clientHeight > 8);
        }
    }, []);

    useEffect(() => {
        updateScrollHint();
    }, [visibleEventsList, updateScrollHint]);

    const scrollToBottom = () => {
        const list = listRef.current;
        if (list) {
            list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
        }
    };

    /**
     * Recap of what the e-mails will contain
     */
    const summaryDetail = () => {
        const parts = [];
        if (selectionSummary.workDates > 0) {
            parts.push(pluralize(selectionSummary.workDates, 'work date'));
        }
        if (selectionSummary.otherEvents > 0) {
            parts.push(pluralize(selectionSummary.otherEvents, 'other event'));
        }
        return parts.length > 0 ? parts.join(' · ') : 'switch on the models to include';
    };

    return (
        <div className="events-selection">
            <div className="events-selection-body">
                <ScopeFilters />
                <div className="events-selection-main" ref={listRef} onScroll={updateScrollHint}>
                    <div className="events-selection-summary">
                        <div className="events-selection-tally">
                            <p className="tally-main">{pluralize(selectionSummary.models, 'model')}</p>
                            <p className="tally-detail">{summaryDetail()}</p>
                        </div>
                        <SelectionBar
                            className="selection-actions"
                            actions={[
                                { label: 'select all', method: actions.selectAllVisibleModels },
                                { label: 'default selection', method: actions.selectDefaultModels },
                            ]}
                        />
                    </div>

                    {visibleEventsList.length === 0 ? (
                        <Alert
                            className="events-selection-message"
                            message="Nothing to send yet. Check the events and the work dates you need on the left."
                            type="warning"
                            showIcon
                        />
                    ) : (
                        <div className="events-list">
                            {visibleEventsList.map((event) => (
                                <EventBlock event={event} key={event.uniqID} />
                            ))}
                        </div>
                    )}

                    {errorSending && (
                        <Alert
                            className="events-selection-message"
                            message="Error"
                            description={errorSending}
                            type="error"
                            showIcon
                        />
                    )}
                </div>
            </div>

            {hasMoreBelow && (
                <button type="button" className="events-selection-more" onClick={scrollToBottom}>
                    <NwIcon icon={byPrefixAndName.fasl['chevron-down']} /> more below
                </button>
            )}

            <div className="events-selection-footer">
                <NwCancelButton onClick={onClose} />
                <NwSaveButton
                    onClick={onGenerateDocVersions}
                    disabled={!hasSelection}
                    loading={isSending}
                    label={hasSelection ? `Create ${pluralize(selectionSummary.models, 'e-mail')}` : 'Create e-mails'}
                />
            </div>
        </div>
    );
};

export default EventsSelectionWrapper;
